import * as React from 'react';
import { useState, useEffect, Fragment } from 'react';

/** Material UI Packages. */
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Chip } from '@mui/material';
import Grid from '@mui/material/Grid2';

/** Material UI Icons. */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';

/** Internal Components. */
import HeaderWithIcon from 'shared/ui/HeaderIcon';
import { operationNow } from 'shared/lib/dateFormatter';

/** Volume Types for the master and sub volume data. */
const volumeTypes = [
    { key: 'il_ocean_import', label: 'OIM' },
    { key: 'il_ocean_export', label: 'OEX' },
    { key: 'il_air_import', label: 'AIM' },
    { key: 'il_air_export', label: 'AEX' },
    { key: 'il_ftl_ltl', label: 'OTH' },
];

/** Global Style */
const centerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
};

/** Global Style */
const volumeTextStyle = {
    fontSize: 11,
    fontWeight: 500,
};

/** Function to handle the current and previous month. */
const handleMonth = () => {
    // 월말에 한국에서 열어도 담당자가 서로 같은 달을 보도록 근무지 달력으로 이름을 붙인다.
    const now = operationNow();
    const currentMonth = now.format('MMM').toUpperCase();
    const previousMonth = now.subtract(1, 'month').format('MMM').toUpperCase();
    return { currentMonth, previousMonth };
};

/** Volume Changes Component
 *  main: Main volume data for companies.
 *  sub: Sub volume data for shipper and consignee.
 */
export default function VolumeChanges({ master, sub }) {
    /** Previous and current month information. */
    const { previousMonth, currentMonth } = handleMonth();

    /** State for the main volume data. */
    const [volumeData, setVolumeData] = useState([]);
    /** State for the sub volume data. */
    const [subVolumeData, setSubVolumeData] = useState([]);

    /** Master Volume Data. */
    useEffect(() => {
        setVolumeData(processVolumeData(master));
    }, [master]);

    /** Sub Volume Data. */
    useEffect(() => {
        setSubVolumeData(processVolumeData(sub));
    }, [sub]);

    /** Process volume data to calculate differences and totals. */
    const processVolumeData = (data) => {
        return data
            .map((company) => {
                const updatedCompany = { ...company };
                let total = 0;
                volumeTypes.forEach(({ key }) => {
                    const typeData = updatedCompany[key];
                    const diff = typeData.current_volume - typeData.previous_volume;
                    total += typeData.current_volume;
                    updatedCompany[key] = {
                        ...typeData,
                        diff,
                    };
                });

                updatedCompany.total = total;
                return updatedCompany;
            })
            .sort((a, b) => b.total - a.total);
    };

    /** Function renders the header for the volume data table. */
    const renderVolumeHeader = () => (
        <>
            <HeaderWithIcon
                icon={<InfoIcon sx={{ fontSize: 20, color: '#00647e' }} />}
                tooltipTitle={
                    'The chart is calculated based on ETD and displays the house volume. For FTL/LTL, the volume is calculated based on BOL.'
                }
                headerName={'Changes in Cargo Volume'}
                sx={{ fontSize: 14, fontWeight: 500, color: '#003a52', mr: 0.5 }}
            />
            <Grid container sx={{ width: '100%', mb: 0.6 }}>
                <Grid size={4}></Grid>
                {volumeTypes.map(({ label }, idx) => (
                    <Grid key={idx} size={1.4} sx={centerStyle}>
                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{label}</Typography>
                    </Grid>
                ))}
            </Grid>
            <Grid container sx={{ width: '100%', px: 2, mb: 1 }}>
                <Grid size={3.9} sx={{ ...centerStyle, justifyContent: 'end', pr: 3 }}>
                    <Typography sx={volumeTextStyle}>TTL</Typography>
                </Grid>
                {volumeTypes.map((type) =>
                    [previousMonth, currentMonth, 'CHG'].map((label) => (
                        <Grid key={`header-${type.key}-${label}`} size={0.49} sx={centerStyle}>
                            <Typography sx={{ fontSize: 10, fontWeight: 500 }}>{label}</Typography>
                        </Grid>
                    ))
                )}
            </Grid>
        </>
    );

    return (
        <Box sx={{ overflow: 'auto', height: { xs: '60vh', lg: '79vh' }, px: 0.5, mb: 0.6 }}>
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    backgroundColor: '#fff',
                }}
            >
                {renderVolumeHeader()}
            </Box>
            {volumeData.map((rank, idx) => {
                const filteredSubData = subVolumeData.filter((item) => item.parent_id === rank.il_trade_partner.id);
                return (
                    <Accordion
                        key={`accordion-${rank.id}-${idx}`}
                        sx={{ border: 'none', mb: 0.7, boxShadow: 2, borderRadius: 1 }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: 40,
                                width: '100%',
                                borderRadius: 1,
                                '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
                                '& .MuiAccordionSummary-content': { paddingY: 0.5, margin: 0 },
                                '&.Mui-expanded': { minHeight: 40 },
                            }}
                        >
                            <Grid container sx={{ width: '100%' }}>
                                <Grid size={3} sx={{ ...centerStyle, justifyContent: 'start' }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                                        {rank.il_trade_partner.name}
                                    </Typography>
                                </Grid>
                                <Grid size={1} sx={centerStyle}>
                                    <Typography
                                        sx={{
                                            ...volumeTextStyle,
                                            ...centerStyle,
                                            backgroundColor: '#e9e9e9',
                                            borderRadius: 10,
                                            width: 42,
                                            height: 24,
                                        }}
                                    >
                                        {rank.total}
                                    </Typography>
                                </Grid>
                                {/*Loop through each volume type to display previous, current, and difference values */}
                                {volumeTypes.map(({ key }) => (
                                    <Fragment key={`row-${rank.id}-${key}`}>
                                        {/* previous_volume */}
                                        <Grid key={`prev-${rank.id}-${key}`} size={0.5} sx={centerStyle}>
                                            <Typography sx={volumeTextStyle}>
                                                {rank[key]?.previous_volume ?? '-'}
                                            </Typography>
                                        </Grid>

                                        {/* current_volume */}
                                        <Grid key={`curr-${rank.id}-${key}`} size={0.5} sx={centerStyle}>
                                            <Typography sx={volumeTextStyle}>
                                                {rank[key]?.current_volume ?? '-'}
                                            </Typography>
                                        </Grid>

                                        {/* diff */}
                                        <Grid key={`diff-${rank.id}-${key}`} size={0.5} sx={centerStyle}>
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    fontWeight: 500,
                                                    color:
                                                        rank[key]?.diff > 0
                                                            ? '#008000'
                                                            : rank[key]?.diff < 0
                                                            ? '#f813600'
                                                            : '#262626',
                                                }}
                                            >
                                                {rank[key]?.diff > 0 ? `+${rank[key].diff}` : rank[key]?.diff ?? '-'}
                                            </Typography>
                                        </Grid>
                                    </Fragment>
                                ))}
                            </Grid>
                        </AccordionSummary>
                        {filteredSubData.length > 0 && (
                            <AccordionDetails sx={{ padding: 0, paddingX: 2, margin: 0, minHeight: 150 }}>
                                {filteredSubData.map((subData, subIdx) => {
                                    return (
                                        <Grid
                                            container
                                            key={`sub-row-${subData.il_trade_partner.id}-${subIdx}`}
                                            sx={{ width: '100%', mb: 0.4 }}
                                        >
                                            {/* Trade Partner Type */}
                                            <Grid size={0.7} sx={centerStyle}>
                                                <Chip
                                                    label={subData.type === 'shipper' ? 'SHIP' : 'CNEE'}
                                                    sx={{
                                                        bgcolor: subData.type === 'shipper' ? '#0042af' : '#ff6d00',
                                                        width: '100%',
                                                        color: '#fff',
                                                        height: 18,
                                                        ...volumeTextStyle,
                                                    }}
                                                    size="small"
                                                />
                                            </Grid>

                                            {/* Shipper / Consignee name */}
                                            <Grid size={2.25} sx={{ ...centerStyle, justifyContent: 'start', pl: 1 }}>
                                                <Typography sx={volumeTextStyle}>
                                                    {subData.il_trade_partner.name}
                                                </Typography>
                                            </Grid>

                                            {/* Total Count */}
                                            <Grid size={0.95} sx={centerStyle}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 11,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {subData.total}
                                                </Typography>
                                            </Grid>

                                            {volumeTypes.map(({ key }) => (
                                                <Fragment key={`sub-${subData.il_trade_partner.id}-${key}-${subIdx}`}>
                                                    {/* previous_volume */}
                                                    <Grid
                                                        key={`sub-prev-${subData.il_trade_partner.id}-${key}-${subIdx}`}
                                                        size={0.48}
                                                        sx={centerStyle}
                                                    >
                                                        <Typography sx={volumeTextStyle}>
                                                            {subData[key]?.previous_volume ?? '-'}
                                                        </Typography>
                                                    </Grid>

                                                    {/* current_volume */}
                                                    <Grid
                                                        key={`sub-curr-${subData.il_trade_partner.id}-${key}-${subIdx}`}
                                                        size={0.48}
                                                        sx={centerStyle}
                                                    >
                                                        <Typography sx={volumeTextStyle}>
                                                            {subData[key]?.current_volume ?? '-'}
                                                        </Typography>
                                                    </Grid>

                                                    {/* diff */}
                                                    <Grid
                                                        key={`sub-diff-${subData.il_trade_partner.id}-${key}-${subIdx}`}
                                                        size={0.5}
                                                        sx={centerStyle}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                ...volumeTextStyle,
                                                                color:
                                                                    subData[key]?.diff > 0
                                                                        ? '#008000'
                                                                        : subData[key]?.diff < 0
                                                                        ? '#f83600'
                                                                        : '#262626',
                                                            }}
                                                        >
                                                            {subData[key]?.diff > 0
                                                                ? `+${subData[key].diff}`
                                                                : subData[key]?.diff ?? '-'}
                                                        </Typography>
                                                    </Grid>
                                                </Fragment>
                                            ))}
                                        </Grid>
                                    );
                                })}
                            </AccordionDetails>
                        )}
                    </Accordion>
                );
            })}
        </Box>
    );
}
